import React, { useState, useRef, useEffect } from 'react';
import { ReactComponent as Chat } from 'design_system/src/static/icons/chat-square-plain-2.svg';
import { ReactComponent as Send } from 'design_system/src/static/icons/paper-plane.svg';
import { ReactComponent as Minus } from 'design_system/src/static/icons/minus-lg.svg';
import sauron from 'static/logos/icono_sauron.svg';
import moment from 'moment';
import Linkify from 'react-linkify';
import { callSauronChat } from 'models/_external/sauron-conversation/controllers/sauron-conversation.controller';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { DeviceLocation } from './GeotileMap.constants';

import './SauronChat.scss';

interface SauronChatProps {
	device: DeviceLocation;
}

interface ChatMessage {
	id: string;
	text: string;
	sender: 'user' | 'system';
	timestamp: Date;
}

const linkDecorator = (
	decoratedHref: string,
	decoratedText: string,
	key: number,
) => (
	<a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer">
		{decoratedText}
	</a>
);

const SauronChat: React.FC<SauronChatProps> = ({ device }) => {
	const [isOpen, setIsOpen] = useState(true);
	const [messages, setMessages] = useState<ChatMessage[]>([
		{
			id: '1',
			text: 'Mi nombre es Sauron. Piensa en mí como un asistente que te ayuda a entender por dónde se mueven tus usuarios, identificar patrones y tomar decisiones con base en datos reales. ¿En qué puedo ayudarte hoy?',
			sender: 'system',
			timestamp: new Date(),
		},
	]);
	const [inputText, setInputText] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [sessionId, setSessionId] = useState<string>('');

	const messagesEndRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages, isLoading]);

	// Generar sessionId único cuando se abre el chat
	useEffect(() => {
		if (isOpen && !sessionId) {
			const newSessionId = `${Date.now()}_${Math.random()
				.toString(36)
				.substr(2, 9)}`;
			setSessionId(newSessionId);
		}
	}, []);

	const toggleChat = () => {
		setIsOpen(!isOpen);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputText(e.target.value);
	};

	const handleSendMessage = () => {
		const message = inputText.trim();
		if (message) {
			const previousMessages = [...messages];
			const newMessage: ChatMessage = {
				id: Date.now().toString(),
				text: message,
				sender: 'user',
				timestamp: new Date(),
			};

			setMessages((prevMessages) => [...prevMessages, newMessage]);
			setInputText('');

			// Simular respuesta del sistema
			setIsLoading(true);
			callSauronChat(
				{
					deviceId: device._id,
					history: moment().diff(moment(device.createdAt), 'days'),
					sessionId,
					message,
				},
				'Sauron',
			)
				.then((response) => {
					const systemResponse: ChatMessage = {
						id: response._id,
						text: response.message,
						sender: 'system',
						timestamp: new Date(),
					};
					setMessages((prevMessages) => [...prevMessages, systemResponse]);
					setIsLoading(false);
				})
				.catch(() => {
					commonErrorHandler('Ocurrio un error al enviar tu mensaje');
					setInputText(message);
					setMessages(previousMessages);
					setIsLoading(false);
				});
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && inputText.trim()) {
			handleSendMessage();
		}
	};

	const formatTime = (date: Date) => {
		return date.toLocaleTimeString('es-ES', {
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	return (
		<div className="sauron-chat">
			<button
				type="button"
				className={`sauron-chat-toggle ${isOpen ? 'active' : ''}`}
				onClick={toggleChat}
				aria-label="Abrir chat de Sauron"
			>
				<Chat className="text_neutral_0" />
				{!isOpen && <span className="notification-dot" />}
			</button>

			{isOpen && (
				<div className="sauron-chat-panel">
					<div className="chat-header">
						<div className="chat-title">
							<div className="chat-avatar">
								<img src={sauron} alt="icon" className="h_xl" />
							</div>
							<div className="chat-info">
								<h3>Sauron AI</h3>
								<p>
									Vehiculo: {device.externalVehicleData?.internalId || 'N/A'}
								</p>
							</div>
						</div>
						<button
							type="button"
							className="close-chat"
							onClick={toggleChat}
							aria-label="Cerrar chat"
						>
							<Minus className="text_neutral_0 dim_lg" />
						</button>
					</div>

					<div className="chat-messages">
						{messages.map((message) => (
							<div
								key={message.id}
								className={`message ${
									message.sender === 'user' ? 'user' : 'system'
								}`}
							>
								<div className="message-content">
									<Linkify componentDecorator={linkDecorator}>
										{message.text}
									</Linkify>
									<span className="message-time">
										{formatTime(message.timestamp)}
									</span>
								</div>
							</div>
						))}

						{isLoading && (
							<div className="message system">
								<div className="message-content typing-indicator">
									<div className="typing-dots">
										<span className="dot" />
										<span className="dot" />
										<span className="dot" />
									</div>
								</div>
							</div>
						)}

						<div ref={messagesEndRef} />
					</div>

					<div className="chat-input">
						<input
							type="text"
							placeholder="Escribe un mensaje..."
							className="message-input"
							value={inputText}
							onChange={handleInputChange}
							onKeyPress={handleKeyPress}
							disabled={isLoading}
						/>
						<button
							type="button"
							className={`send-button ${
								!inputText.trim() || isLoading ? 'disabled' : ''
							}`}
							onClick={handleSendMessage}
							disabled={!inputText.trim() || isLoading}
							aria-label="Enviar mensaje"
						>
							<Send className="text_neutral_0 dim_lg" />
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default SauronChat;
